import React from 'react';
import { Icon } from '@shopify/polaris';
import { StarFilledIcon } from '@shopify/polaris-icons';
import Wordmark from '../Wordmark';
import { APP_LOGO, APP_NAME } from '../ui/appBrand';
import { REVIEWS } from './PricingTrustFaq';

/**
 * Branded masthead above the billing toggle: app icon + wordmark, one line of
 * positioning, and the one credential this product can actually back.
 *
 * The headline is a description, not a ranking. "#1 app for..." needs a named
 * source - a category chart, a dated ranking - or it is unsubstantiated, and
 * this is the page where someone enters payment details.
 *
 * DIFFERENT FROM THE SHOPIFY APP'S COPY OF THIS FILE, deliberately:
 *   - No "Built for Shopify" badge. That badge is awarded to a Shopify App
 *     Store listing. This product is sold outside that store, so showing it
 *     here would claim a credential this install does not hold.
 *   - The headline does not say "Built for Shopify POS", because this app has
 *     no POS.
 *   - The rating names where it comes from. It is a real figure about this
 *     product, and the only honest way to show it on a page a non-Shopify
 *     merchant is reading is to say which storefront it was earned on.
 */

const INK = '#303030';
const MUTED = '#616161';
const STAR = '#ffb800';

function Credential({ icon, color, children, href }) {
  const Tag = href ? 'a' : 'span';
  const linkProps = href
    ? { href, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <Tag
      {...linkProps}
      className="inline-flex items-center gap-1.5 text-[13px] leading-none no-underline hover:underline"
      style={{ color: MUTED }}
    >
      {/* .Polaris-Icon carries margin:auto; the sized flex-none box contains it
          so the label that follows is not pushed to the far end of the row. */}
      <span className="inline-flex h-4 w-4 flex-none items-center justify-center" style={{ color }}>
        <Icon source={icon} tone="inherit" />
      </span>
      {children}
    </Tag>
  );
}

export default function PricingHeader({ t }) {
  return (
    <div className="flex flex-col items-center px-4 pb-2 pt-1 text-center">
      <div className="flex items-center gap-2.5">
        <img
          src={APP_LOGO}
          alt={APP_NAME}
          width={36}
          height={36}
          className="h-9 w-9 flex-none rounded-[9px]"
          style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.06)' }}
        />
        <Wordmark className="text-[21px]" />
      </div>

      <h1 className="mb-0 mt-3 text-[22px] font-bold leading-tight tracking-[-0.01em]" style={{ color: INK }}>
        {t('pricing_hero_title')}
      </h1>

      <div className="mt-2.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
        <Credential icon={StarFilledIcon} color={STAR} href={REVIEWS.listingUrl}>
          {t('pricing_hero_rating', { rating: REVIEWS.rating, reviews: REVIEWS.count })}
        </Credential>
      </div>
    </div>
  );
}
